import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

const fontSx = {
  fontSize: ["0.75rem", "0.75rem", "0.75rem", "0.75rem", "1rem", "1rem"],
};

// Fixed for every membership this panel writes. Shown, so it is clear what is
// being granted, but not editable - the backend sets both regardless of what is
// sent, and offering a choice the server ignores would be a lie.
const ROLE = "member";
const PRODUCT = "IDX";

/**
 * Manage a user's IDX organizations.
 *
 * Replaces the old approve dialog. Approving could only ever set a first
 * organization; this can add, remove, and show what someone already has, and
 * granting an unaffiliated user their first organization is just the same
 * operation with an empty starting list.
 *
 * Nothing is written until Save. Removing every organization is a legitimate
 * outcome - it returns the user to Unaffiliated - so it is allowed, with the
 * consequence spelled out rather than blocked.
 */
const ManagePermissionsDialog = ({ user, organizations, setUser, onConfirm }) => {
  const [orgs, setOrgs] = useState([]);
  const [toAdd, setToAdd] = useState("");

  // Reset whenever a different user is opened, so an abandoned edit cannot leak
  // into the next one.
  useEffect(() => {
    setOrgs((user?.memberships || []).map((m) => m.org));
    setToAdd("");
  }, [user]);

  if (!user) return null;

  const orgOptions = Object.values(organizations || {})
    .map(({ display_name, uuid }) => ({ value: uuid, label: display_name || uuid }))
    .filter((o) => !orgs.includes(o.value))
    .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

  const nameFor = (uuid) => organizations?.[uuid]?.display_name || uuid;

  const original = (user.memberships || []).map((m) => m.org);
  const dirty =
    orgs.length !== original.length || orgs.some((o) => !original.includes(o));

  const handleClose = () => {
    setUser(null);
  };

  return (
    <Dialog
      open={!!user}
      onClose={handleClose}
      aria-labelledby="manage-permissions-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="manage-permissions-dialog">
        {`Manage Permissions — ${user.name || user.email}`}
      </DialogTitle>
      <DialogContent>
        <Typography sx={fontSx} className={"text-gray-600"}>
          {orgs.length === 0
            ? "This user belongs to no organizations and will appear under Unaffiliated."
            : "Organizations this user belongs to."}
        </Typography>

        {orgs.length > 0 && (
          <Table size="small" aria-label="memberships">
            <TableHead>
              <TableRow>
                <TableCell sx={fontSx}>Organization</TableCell>
                <TableCell sx={fontSx}>Role</TableCell>
                <TableCell sx={fontSx}>Product</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((uuid) => (
                <TableRow key={uuid}>
                  <TableCell sx={fontSx}>{nameFor(uuid)}</TableCell>
                  <TableCell sx={{ ...fontSx, color: "text.disabled" }}>
                    {ROLE}
                  </TableCell>
                  <TableCell sx={{ ...fontSx, color: "text.disabled" }}>
                    {PRODUCT}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Remove ${nameFor(uuid)}`}
                      onClick={() =>
                        setOrgs(orgs.filter((o) => o !== uuid))
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            id="add-organization"
            select
            size="small"
            label="Add organization"
            value={toAdd}
            onChange={(event) => setToAdd(event.target.value)}
            sx={{ minWidth: "20ch", flex: 1 }}
            disabled={orgOptions.length === 0}
            helperText={
              orgOptions.length === 0
                ? "This user is already in every organization."
                : " "
            }
          >
            {orgOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            disabled={!toAdd}
            onClick={() => {
              setOrgs([...orgs, toAdd]);
              setToAdd("");
            }}
          >
            Add
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleClose}
          color="secondary"
          id={"dialog-cancel"}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!dirty}
          onClick={() => {
            const next = orgs;
            handleClose();
            onConfirm(user, next);
          }}
          id={"dialog-confirm"}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagePermissionsDialog;
